import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RouterIcon from '@mui/icons-material/Router';
import { Box, Chip, type ChipProps, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { type RouterNetworkResponse } from '@okvc/schema';

import { useRouterNetwork } from '../api/index.js';
import { useCheckPermissions } from '../auth/index.js';
import { Loading } from '../components/index.js';
import RouterCapacityTestTab from '../components/Router/Detail/RouterCapacityTestTab.js';
import RouterEventsTab from '../components/Router/Detail/RouterEventsTab.js';
import RouterOverviewTab from '../components/Router/Detail/RouterOverviewTab.js';
import { networkLabel } from '../components/Router/RoutersTable.js';

type RouterDetailTab = 'overview' | 'events' | 'test';

const DEFAULT_DAYS = 7;
const BROWSER_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

function headerChipColor(state: RouterNetworkResponse['router']['networkState']): ChipProps['color'] {
  if (state === 'offline') {
    return 'error';
  }
  return state === 'switching' ? 'info' : 'default';
}

function headerNetworkLabel({ router, sims }: RouterNetworkResponse): string {
  const activeSim = sims.find((sim) => sim.isActive);
  return networkLabel({ networkState: router.networkState, activeSim: activeSim ?? null });
}

const RouterDetail = () => {
  const { routerId = '' } = useParams<{ routerId: string }>();
  const [tab, setTab] = useState<RouterDetailTab>('overview');
  const [days, setDays] = useState(DEFAULT_DAYS);
  const { canUpdateRouter } = useCheckPermissions();
  const { data: network, isLoading, isError } = useRouterNetwork(routerId, { days, timezone: BROWSER_TIME_ZONE });

  if (isLoading) {
    return <Loading />;
  }
  if (isError || !network) {
    return <Typography sx={{ m: 2 }}>This router could not be loaded.</Typography>;
  }

  const { router } = network;
  return (
    <Box sx={{ m: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton component={Link} to="/routers" aria-label="Back to routers">
          <ArrowBackIcon />
        </IconButton>
        <RouterIcon color="primary" />
        <Typography variant="h4" component="h1">{router.name}</Typography>
        <Chip label={headerNetworkLabel(network)} color={headerChipColor(router.networkState)} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {[
          router.patientName ?? 'No patient assigned',
          router.lastSeenAt ? `last reached ${DateTime.fromISO(router.lastSeenAt).toRelative()}` : null,
        ].filter(Boolean).join(' · ')}
      </Typography>

      <Tabs value={tab} onChange={(_, value: RouterDetailTab) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="overview" label="Overview" />
        <Tab value="events" label="Events" />
        <Tab value="test" label="Network test" />
      </Tabs>

      {tab === 'overview' ? (
        <RouterOverviewTab
          routerId={routerId}
          network={network}
          days={days}
          onDaysChange={setDays}
          canSwitchSim={canUpdateRouter}
        />
      ) : null}
      {tab === 'events' ? <RouterEventsTab events={network.events} /> : null}
      {tab === 'test' ? <RouterCapacityTestTab routerId={routerId} canRunTest={canUpdateRouter} /> : null}
    </Box>
  );
};

export default RouterDetail;
